// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The shell: everything the page is before a file arrives, plus the renderers
 * for what appears after one does.
 *
 * The layout is deliberately a reading interface rather than a dashboard. The
 * primary object on screen is a paragraph of source text with a span highlighted
 * in it, because that — not a percentage — is what lets somebody decide whether
 * their quotation is right.
 */

import {
  CLASS_BLURBS,
  CLASS_LABELS,
  EXACTNESS_CAVEAT,
  HEADLINE_NOTE,
  IDENTIFIER_INCONCLUSIVE,
  IDENTIFIER_RATE_LIMITED_NOTE,
  IDENTIFIER_TABLE_CAVEAT,
  IDENTIFIER_UNREACHABLE_CAVEAT,
  IDENTIFIER_UNREACHABLE_NOTE,
  IDENTIFIER_VERDICT_LABELS,
  NOT_FOUND_CAVEAT,
  ORTHOGRAPHIC_NOTE,
  PREFLIGHT_HEADING,
  SHEET_ONLY_NOTE,
  TAGLINE,
  TRUST_BANNER,
  sourceNoteLines,
} from './content.ts';
import { term } from './glossary.ts';
import type { DraftQuote, ExtractedSource, IdentifierResult, QuoteResult } from './types.ts';

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ── intake rules ──────────────────────────────────────────────────────────
 *
 * Every rule about what this tool will accept lives here rather than in
 * main.ts, for one reason: main.ts touches the DOM at module scope, so the
 * unit suite (`environment: 'node'`, no DOM) cannot import it. Stating the
 * rules in a file that IS importable is what lets tests/regress-d.test.ts hold
 * them still.
 */

export const DRAFT_EXT = /\.(md|markdown|txt|text|docx)$/i;
export const SOURCE_EXT = /\.(pdf|docx|epub|txt|text|md|markdown|html|htm)$/i;
export const ARCHIVE_EXT = /\.zip$/i;

export function isDraftName(name: string): boolean {
  return DRAFT_EXT.test(name);
}

export function isSourceName(name: string): boolean {
  return SOURCE_EXT.test(name);
}

export function isArchiveName(name: string): boolean {
  return ARCHIVE_EXT.test(name);
}

/*
 * Paths inside a folder or an archive that must never become source files.
 *
 * A zip made by the macOS Finder carries a parallel `__MACOSX/` tree of
 * AppleDouble stubs, and `__MACOSX/._paper.pdf` ends in `.pdf` — so a plain
 * extension test admitted a 4 KB blob of resource-fork metadata to the corpus,
 * where it was read as an unreadable PDF and counted against the user. Zip
 * directory entries (trailing slash, zero bytes) did the same.
 */
export function isJunkPath(path: string): boolean {
  if (path.endsWith('/')) return true;
  if (/(^|\/)__MACOSX(\/|$)/.test(path)) return true;
  const name = path.split('/').pop() ?? path;
  return name === '' || name.startsWith('.');
}

/** The entries of an archive or a walked folder that are worth keeping. */
export function archiveEntriesToKeep(names: string[]): string[] {
  return names.filter((name) => !isJunkPath(name) && isSourceName(name));
}

/*
 * A paste anywhere on the page loads a draft — except into something the user
 * is typing in. The guard used to exempt only `tagName === 'INPUT'`, so a
 * paste into ANY textarea in the document was preventDefault()ed (the textarea
 * read as broken) and silently replaced the already-loaded draft with the
 * pasted text. The feedback widget in the footer is exactly that case: it
 * mounts a real <dialog> containing a <textarea> into THIS document, not into
 * an iframe, and it neither listens for paste nor stops propagation.
 */
export const EDITABLE_TARGET = 'input, textarea, select, [contenteditable], dialog';

export function isEditableTarget(target: unknown): boolean {
  const node = target as { closest?: (selector: string) => unknown } | null;
  if (!node || typeof node.closest !== 'function') return false;
  return node.closest(EDITABLE_TARGET) != null;
}

/*
 * The two states of the online-identifier switch. They belong next to the
 * markup that swaps them rather than in content.ts, because they are a readout
 * of a control rather than a claim the tool makes about itself; the claim
 * itself (what is sent, and what is never sent) is the static sentence beside
 * them and is not toggled.
 */
export const ONLINE_STATE_OFF = 'Off — quote checking is entirely offline, and nothing leaves this tab.';
export const ONLINE_STATE_ON = 'On — the identifiers in your draft will be sent when you run the check.';

export const SHELL = `
<header class="site-header">
  <div class="header-inner">
    <div class="brand">
      <svg class="brand-mark" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
        <rect x="1" y="1" width="30" height="30" rx="8" fill="currentColor" opacity="0.08"/>
        <path d="M9.5 9.5h5.2v5.2c0 2.9-1.8 4.9-4.6 5.6v-2.3c1.4-.5 2.1-1.5 2.2-2.8H9.5V9.5Zm8.3 0H23v5.2c0 2.9-1.8 4.9-4.6 5.6v-2.3c1.4-.5 2.1-1.5 2.2-2.8h-2.8V9.5Z" fill="currentColor"/>
        <path d="M8 24.2h16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
      </svg>
      <div>
        <h1>quotewitness</h1>
        <p>${TAGLINE}</p>
      </div>
    </div>
    <nav class="header-nav">
      <button type="button" class="ghost-button" data-modal="how">How it works</button>
      <button type="button" class="ghost-button" data-modal="privacy">Privacy</button>
      <button type="button" class="ghost-button" data-modal="about">About</button>
      <button type="button" class="ghost-button drawer-toggle" id="log-toggle" aria-expanded="false" aria-controls="event-drawer">
        Log <span class="badge" id="log-badge" hidden></span>
      </button>
    </nav>
  </div>
</header>

<main class="main-content">
  <p class="trust-banner">
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3l7 3v6c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6l7-3Z"/></svg>
    ${TRUST_BANNER}
    <button type="button" class="link-button" data-modal="privacy">What that does and does not cover</button>
  </p>

  <!-- The zones are plain containers. They used to be
       a div carrying role=button plus an aria-label, wrapping the heading,
       the format hints and the ✓ file list — and role="button" carries ARIA's
       children-presentational rule, so WebKit pruned that entire subtree from
       the accessibility tree and the author-supplied aria-label overrode it.
       A VoiceOver user heard an identical announcement before and after
       dropping a file, and the list of what was accepted did not exist for
       them. The real control is now the button inside each zone; the
       card stays clickable and droppable as a convenience on top of it. -->
  <section class="intake" id="intake">
    <div class="drop" id="drop-draft">
      <span class="drop-step" aria-hidden="true">1</span>
      <h2>Your draft</h2>
      <p class="drop-hint">Drag it here, or paste it with <kbd>⌘V</kbd></p>
      <p class="drop-formats">.md · .txt · .docx</p>
      <p class="drop-pick"><button type="button" class="drop-choose" id="pick-draft">Choose a draft…</button></p>
      <ul class="file-list" id="draft-list"></ul>
      <input type="file" id="draft-input" accept=".md,.markdown,.txt,.text,.docx" hidden>
    </div>

    <div class="drop" id="drop-sources">
      <span class="drop-step" aria-hidden="true">2</span>
      <h2>The sources you hold</h2>
      <p class="drop-hint">Drag the whole folder of them here — a .zip of it works too</p>
      <p class="drop-formats">.pdf · .docx · .epub · .txt · .md · .html · .zip</p>
      <p class="drop-pick">
        <button type="button" class="drop-choose" id="pick-sources">Choose files…</button>
        <button type="button" class="drop-choose" id="pick-source-folder" hidden>Choose a folder…</button>
      </p>
      <ul class="file-list" id="source-list"></ul>
      <input type="file" id="source-input" accept=".pdf,.docx,.epub,.txt,.text,.md,.markdown,.html,.htm,.zip" multiple hidden>
      <input type="file" id="source-folder-input" multiple hidden>
    </div>
  </section>

  <div class="actions">
    <button type="button" class="primary-button" id="run" disabled>Check the quotations</button>
    <button type="button" class="sample-button" id="sample">Try a sample</button>
    <label class="online-toggle">
      <input type="checkbox" id="online">
      <span>
        <strong>Check identifiers online</strong>
        <!-- This sentence is a readout of the switch, not a fixed assertion: it
             used to begin with a hardcoded "Off." that stayed there after the
             box was ticked, so the control contradicted itself. -->
        <small id="online-state">${ONLINE_STATE_OFF}</small>
        <!-- FOUR registries, not five. api.semanticscholar.org was named here,
             in the CSP, in the privacy modal and in the README, but no code
             path ever contacted it. A privacy control that overstates where
             data goes is as wrong as one that understates it. -->
        <small>Ticking it sends <em>only</em> the DOIs, PMIDs and arXiv ids from your draft — never the quotations, never the draft text, never any part of your source files — to four public registries, so I can check that each identifier points at the work you say it does.</small>
      </span>
    </label>
  </div>

  <!-- The single announcement channel. Before this existed the only aria-live
       in the codebase was inside the event drawer, which is created hidden —
       a display:none subtree is not in the accessibility tree, so nothing that
       happened on this page was ever announced. Written at milestones only:
       per-page progress would flood a screen reader with hundreds of lines. -->
  <p class="sr-only" id="status" role="status" aria-live="polite" aria-atomic="true"></p>

  <div class="progress" id="progress" hidden>
    <div class="progress-bar" id="progress-bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" aria-labelledby="progress-label">
      <div class="progress-fill" id="progress-fill"></div>
    </div>
    <p class="progress-label" id="progress-label">Reading…</p>
  </div>

  <p class="error" id="error" role="alert" hidden></p>

  <section id="results" hidden></section>
</main>

<footer class="site-footer">
  <div class="footer-inner">
    Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
    · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
  </div>
</footer>`;

export function renderPreflight(sources: ExtractedSource[]): string {
  const rows = sources
    .map((source) => {
      /*
       * The lines themselves come from content.ts's sourceNoteLines(), which
       * the exported HTML report also uses. They used to be assembled twice —
       * and the report's copy quietly said less, so a PDF with eight scanned
       * sheets read in the forwarded artefact as though every page had been
       * searched. This function now only decides how each line is PAINTED.
       */
      const lines = sourceNoteLines(source);
      const notes = lines.map((line, index) => {
        // Line 0 is the whole story when the file could not be read at all.
        if (index === 0 && (source.error || source.unsearchable)) {
          return `<span class="bad">${escapeHtml(line)}</span>`;
        }
        // Pages with no text layer are the one thing here that changes what a
        // NOT FOUND means, so they are the only warn-coloured line.
        if (/no text layer/.test(line)) return `<span class="warn">${escapeHtml(line)}</span>`;
        if (/removed before matching\.$/.test(line)) return `<span class="quiet">${escapeHtml(line)}</span>`;
        return escapeHtml(line);
      });
      return `<tr>
        <td class="file">${escapeHtml(source.name)}<span class="quiet"> · ${formatBytes(source.bytes)}</span></td>
        <td class="num">${source.pages.length}</td>
        <td class="num">${source.text.length.toLocaleString()}</td>
        <td class="note">${notes.join('<br>')}</td>
      </tr>`;
    })
    .join('');

  return `<div class="panel">
    <h2>${PREFLIGHT_HEADING}</h2>
    <div class="table-wrap"><table class="preflight">
      <thead><tr><th>File</th><th class="num">Pages</th><th class="num">Characters</th><th>What I can tell you about it</th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div>
  </div>`;
}

function renderDiff(result: QuoteResult): string {
  const parts = result.diff.map((edit) => {
    if (edit.kind === 'same') return escapeHtml(edit.source ?? '');
    if (edit.kind === 'orthographic') {
      return `<span class="d-ortho" title="spelling only">${escapeHtml(edit.source ?? '')}</span>`;
    }
    if (edit.kind === 'insertion') return `<ins>${escapeHtml(edit.quote ?? '')}</ins>`;
    if (edit.kind === 'deletion') return `<del>${escapeHtml(edit.source ?? '')}</del>`;
    return `<del>${escapeHtml(edit.source ?? '')}</del> <ins>${escapeHtml(edit.quote ?? '')}</ins>`;
  });
  return parts.join(' ');
}

function renderWhere(result: QuoteResult, source: ExtractedSource | null): string {
  if (!source) return '';
  const page = source.pages.find((p) => p.sheet === result.sheet);
  const bits: string[] = [`<strong>${escapeHtml(source.name)}</strong>`];
  if (result.printedPage) {
    bits.push(`printed p.&nbsp;${escapeHtml(result.printedPage)}`);
    if (page?.from === 'conflict') {
      bits.push(
        `<span class="warn">the file's own labels say ${escapeHtml(page.labelled ?? '?')} here, so I will not judge your pinpoint</span>`,
      );
    }
  } else if (result.sheet !== null && source.kind === 'pdf') {
    bits.push(`PDF sheet&nbsp;${result.sheet}`);
    bits.push(`<span class="quiet">${SHEET_ONLY_NOTE}</span>`);
  } else if (source.kind === 'epub') {
    bits.push(`chapter&nbsp;${result.sheet}`);
  }
  return bits.join(' · ');
}

/*
 * The tally and the cards are rendered separately from the frame around them
 * because moving the near-match slider re-classifies everything. The whole
 * results panel used to be re-written with one innerHTML assignment, which
 * destroyed the live <input type="range"> the user was still dragging: focus
 * fell to <body> after a single arrow key, and the following scrollIntoView
 * threw the control 358px below the fold. Only these two blocks change when
 * the boundary moves, so only these two are replaced.
 */
export function renderTally(results: QuoteResult[]): string {
  const verified = results.filter((r) => r.cls === 'exact' || r.cls === 'exact-ellipsis').length;
  const attention = results.filter((r) => ['near', 'altered', 'elsewhere', 'not-found'].includes(r.cls)).length;
  const unchecked = results.filter((r) => r.cls === 'not-checked' || r.cls === 'unreadable').length;
  return `
      <div><b>${results.length}</b><span>quotations checked</span></div>
      <div><b class="good">${verified}</b><span>verified</span></div>
      <div><b class="bad">${attention}</b><span>need attention</span></div>
      <div><b class="quiet">${unchecked}</b><span>not checked</span></div>`;
}

type Searched = QuoteResult['searched'][number];

/**
 * How one file is described in the NOT FOUND card's "What I searched" list.
 *
 * There are two ways a file can be unsearchable and they need different words.
 * This used to say "no text layer, not searched" about both, which is a false
 * description of a file that never opened — its text layer is unknown, not
 * absent — and it pointed the reader at OCR when the real answer was a
 * password. The `error` carries the reason the file would not open.
 */
export function searchedLine(s: Searched): string {
  if (s.error) return `could not be opened, so not searched — ${s.error}`;
  if (s.unsearchable) return 'no text layer, not searched';
  return `${s.chars.toLocaleString()} characters`;
}

/**
 * What to suggest when every source that could hold this quotation was
 * unreadable. Advising OCR on an encrypted or corrupt file sends its owner off
 * to fix something that is not broken, so the advice follows the actual reason.
 */
export function unreadableAdvice(searched: Searched[]): string {
  const blocked = searched.filter((s) => s.unsearchable);
  const unopened = blocked.some((s) => s.error);
  const scanned = blocked.some((s) => !s.error);
  if (unopened && scanned) {
    return 'Unlock or repair the file I could not open, give me a text version of the scan — or run it through OCR — and I can answer.';
  }
  if (unopened) return 'Unlock or repair that file, or give me a copy I can open, and I can answer.';
  return 'Give me a text version of that file, or run it through OCR, and I can answer.';
}

export function renderCards(
  quotes: DraftQuote[],
  results: QuoteResult[],
  sources: ExtractedSource[],
): string {
  const byId = new Map(sources.map((s) => [s.id, s]));

  const order: Record<string, number> = {
    'not-found': 0, altered: 1, elsewhere: 2, near: 3, unreadable: 4, 'exact-ellipsis': 5, exact: 6, 'not-checked': 7,
  };
  const sorted = [...results].sort(
    (a, b) => (order[a.cls] - order[b.cls]) || (Number(b.pinpointMismatch) - Number(a.pinpointMismatch)) || a.quoteId - b.quoteId,
  );

  const cards = sorted
    .map((result) => {
      const quote = quotes[result.quoteId];
      const source = result.sourceId != null ? byId.get(result.sourceId) ?? null : null;
      const context = result.context;
      return `<article class="result r-${result.cls}">
        <div class="result-head">
          <span class="verdict v-${result.cls}">${CLASS_LABELS[result.cls]}</span>
          ${result.orthographic ? `<span class="chip" title="${escapeHtml(ORTHOGRAPHIC_NOTE)}">spelling only</span>` : ''}
          ${result.pinpointMismatch ? '<span class="chip chip-warn">pinpoint differs</span>' : ''}
          <span class="result-index">#${result.quoteId + 1}</span>
        </div>
        <blockquote class="quoted">${escapeHtml(quote?.text ?? '')}</blockquote>
        <p class="result-where">${renderWhere(result, source)}</p>
        ${
          result.pinpointMismatch && quote?.pinpoint
            ? `<p class="pinpoint">Cited ${escapeHtml(quote.pinpoint.raw)}. Found on printed page ${escapeHtml(
                result.printedPage ?? '',
              )}${result.sheet ? ` (PDF sheet ${result.sheet})` : ''}.</p>`
            : ''
        }
        ${
          context
            ? `<div class="context"><span class="ctx-side">${escapeHtml(context.before)}</span> <mark>${escapeHtml(
                context.matched,
              )}</mark> <span class="ctx-side">${escapeHtml(context.after)}</span></div>`
            : ''
        }
        ${result.diff.some((d) => d.kind !== 'same') ? `<div class="diff">${renderDiff(result)}</div>` : ''}
        ${
          result.cls === 'unreadable'
            ? `<p class="caveat">${escapeHtml(result.skipped ?? '')}. ${escapeHtml(unreadableAdvice(result.searched))}</p>`
            : ''
        }
        ${
          result.cls === 'not-found'
            ? `${result.blindPages ? `<p class="caveat warn">${escapeHtml(result.blindPages)}</p>` : ''}
               <p class="caveat">${escapeHtml(NOT_FOUND_CAVEAT)}</p>
               <details><summary>What I searched</summary><ul class="searched">${result.searched
                 .map(
                   (s) =>
                     `<li>${escapeHtml(s.name)} — ${escapeHtml(searchedLine(s))}</li>`,
                 )
                 .join('')}</ul></details>`
            : ''
        }
        ${result.cls === 'not-checked' && result.skipped ? `<p class="caveat">Not checked: ${escapeHtml(result.skipped)}.</p>` : ''}
        <p class="blurb">${CLASS_BLURBS[result.cls]}</p>
      </article>`;
    })
    .join('');

  return cards;
}

export function renderResults(
  quotes: DraftQuote[],
  results: QuoteResult[],
  sources: ExtractedSource[],
  identifiers: IdentifierResult[],
): string {
  /*
   * A verdict that is a fact about the network is not painted like a verdict
   * about the user's citation. The ternary here used to send everything that
   * was not `match` or `unresolvable` to v-near (amber), which put "I could
   * not reach a registry" in the same visual register as "the title differs" —
   * and unresolvable's red is the register of a fabricated quotation. Anything
   * in IDENTIFIER_INCONCLUSIVE is neutral grey, because nothing was measured.
   */
  const verdictClass = (verdict: IdentifierResult['verdict']): string => {
    if (verdict === 'match') return 'exact';
    if (verdict === 'unresolvable') return 'not-found';
    if (IDENTIFIER_INCONCLUSIVE.includes(verdict)) return 'not-checked';
    return 'near';
  };
  const unreachable = identifiers.filter((r) => r.verdict === 'not-checked').length;

  const identifierBlock =
    identifiers.length === 0
      ? ''
      : `<div class="panel">
          <h2>Identifiers</h2>
          <p class="caveat">${escapeHtml(IDENTIFIER_TABLE_CAVEAT)}</p>
          ${unreachable > 0 ? `<p class="caveat">${escapeHtml(IDENTIFIER_UNREACHABLE_CAVEAT(unreachable))}</p>` : ''}
          <div class="table-wrap"><table class="preflight"><thead><tr><th>Identifier</th><th>Verdict</th><th>Resolves to</th></tr></thead><tbody>
          ${identifiers
            .map(
              (r) => `<tr><td class="file">${escapeHtml(r.identifier.raw)}</td>
                <td><span class="verdict v-${verdictClass(r.verdict)}">${escapeHtml(
                  IDENTIFIER_VERDICT_LABELS[r.verdict],
                )}</span></td>
                <td class="note">${
                  r.verdict === 'not-checked'
                    ? `<span class="quiet">${escapeHtml(IDENTIFIER_UNREACHABLE_NOTE)}</span>`
                    : r.verdict === 'rate-limited'
                      ? `<span class="quiet">${escapeHtml(IDENTIFIER_RATE_LIMITED_NOTE)}</span>`
                      : `${escapeHtml(r.resolvedTitle ?? '—')}${
                          r.registry ? `<span class="quiet"> · ${escapeHtml(r.registry)}</span>` : ''
                        }`
                }</td></tr>`,
            )
            .join('')}
          </tbody></table></div>
        </div>`;

  return `
    <div class="tally" id="tally">${renderTally(results)}</div>
    <p class="headline-note">${HEADLINE_NOTE}</p>
    <p class="caveat">${escapeHtml(EXACTNESS_CAVEAT)}</p>

    <div class="downloads">
      <button type="button" class="primary-button" id="dl-report">Download the report</button>
      <button type="button" class="ghost-button" id="dl-csv">Download the CSV</button>
      <button type="button" class="ghost-button" id="share" hidden>Share</button>
      <button type="button" class="ghost-button" id="copy-summary">Copy summary</button>
    </div>

    <div class="tune">
      <label for="near">Where a near match becomes “substantially altered”</label>
      <input type="range" id="near" min="5" max="30" step="1" value="10">
      <output id="near-out">10%</output>
      <!-- Re-checking reports itself here rather than in the page-level
           progress bar: that bar sits above the results, so unhiding it pushed
           the slider down while the thumb was still under the user's finger.
           Not a live region — it ticks once per quotation, and #status
           announces the outcome once when the re-check finishes. -->
      <span class="tune-status" id="tune-status"></span>
      <p class="quiet">There is no ground truth for this boundary, so it is yours to set. The differences are always shown either way — read them rather than trusting the label. ${term(
        'n-gram',
      )} matching and ${term('semi-global alignment')} do not change; only where the line is drawn does.</p>
    </div>

    ${identifierBlock}
    <div class="results-list" id="results-list">${renderCards(quotes, results, sources)}</div>`;
}
